<div class="fadein animation-duration-200">
    @if (message()) {
        <div class="surface-card border-1 surface-border border-round p-3 mb-3 text-sm text-700">
            <i class="pi pi-info-circle mr-2 text-primary"></i>{{ message() }}
        </div>
    }

    <div class="grid mb-3">
        <div class="col-12 md:col-3">
            <div class="surface-card border-1 surface-border border-round p-3 h-full summary-tile">
                <div class="summary-kicker">Order Releases</div>
                <div class="summary-value">{{ releases().length }}</div>
                <div class="summary-hint">{{ readyReleases().length }} ready for planning</div>
            </div>
        </div>
        <div class="col-12 md:col-3">
            <div class="surface-card border-1 surface-border border-round p-3 h-full summary-tile">
                <div class="summary-kicker">Shipment Linked</div>
                <div class="summary-value">{{ shipmentLinkedReleases().length }}</div>
                <div class="summary-hint">{{ releases().length - shipmentLinkedReleases().length }} still not linked</div>
            </div>
        </div>
        <div class="col-12 md:col-3">
            <div class="surface-card border-1 surface-border border-round p-3 h-full summary-tile">
                <div class="summary-kicker">Truck Planned</div>
                <div class="summary-value">{{ truckAssignedReleases().length }}</div>
                <div class="summary-hint">{{ assignedTruckCount() }} distinct truck assignments</div>
            </div>
        </div>
        <div class="col-12 md:col-3">
            <div class="surface-card border-1 surface-border border-round p-3 h-full summary-tile">
                <div class="summary-kicker">Planning Blockers</div>
                <div class="summary-value">{{ validationErrorReleases().length }}</div>
                <div class="summary-hint">Validation or release-window issues</div>
            </div>
        </div>
    </div>

    <app-page-data-panel
        title="Order Releases"
        subtitle="Transport releases generated from POSM delivery demand, enriched with truck planning and shipment linkage."
        [resultText]="releases().length + ' releases across ' + releaseGroups().length + ' groups'">

        @if (isLoading() && !releaseGroups().length) {
            <div class="surface-card border-1 surface-border border-round p-4 text-sm text-500">
                Loading grouped order releases...
            </div>
        } @else if (!releaseGroups().length) {
            <div class="surface-card border-1 surface-border border-round p-5 text-center text-500">
                No order releases generated for this project.
            </div>
        } @else {
            <div class="release-directory-scroll">
            <p-table [value]="releaseGroups()" dataKey="key" [rowHover]="true"
                styleClass="p-datatable-sm release-directory-table" [tableStyle]="{ 'min-width': '88rem' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th class="directory-controls">Expand</th>
                        <th>Release &amp; schedule</th>
                        <th>Pickup</th>
                        <th>Dropoff</th>
                        <th>Item / load</th>
                        <th>Need by &amp; status</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-group>
                    <tr>
                        <td>
                            <button type="button" class="directory-expand" [attr.aria-expanded]="isGroupExpanded(group.key)"
                                [attr.aria-label]="(isGroupExpanded(group.key) ? 'Collapse ' : 'Expand ') + (groupRelease(group).releaseNumber || 'release')"
                                (click)="toggleGroup(group.key)">
                                <i class="pi" [ngClass]="isGroupExpanded(group.key) ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
                            </button>
                        </td>
                        <td>
                            <div class="directory-cell">
                                @if (group.parent?.releaseId) {
                                    <a class="release-link" [routerLink]="['/transport-planning/releases', group.parent?.releaseId]">
                                        {{ group.parent?.releaseNumber || ('Release #' + group.parent?.releaseId) }}
                                    </a>
                                } @else {
                                    <span class="missing-parent">Parent #{{ group.parentReleaseId || '-' }}</span>
                                }
                                <p-tag [value]="roleLabel(groupRelease(group))" [severity]="roleSeverity(groupRelease(group))"></p-tag>
                                @if (group.children.length) { <span class="more-lines">{{ groupChildCountLabel(group) }}</span> }
                                <small>{{ groupTitle(group) }}</small>
                                <small>POSM Delivery Schedule: {{ collapsedScheduleNumber(groupRelease(group)) }}</small>
                            </div>
                        </td>
                        <td><div class="directory-cell"><strong>{{ pickupStoreLabel(groupRelease(group)) }}</strong><small>{{ pickupStoreDetail(groupRelease(group)) }}</small></div></td>
                        <td><div class="directory-cell"><strong>{{ dropoffStoreLabel(groupRelease(group)) }}</strong><small>{{ dropoffStoreDetail(groupRelease(group)) }}</small></div></td>
                        <td>
                            <div class="directory-cell">
                                <strong>{{ releaseItemDescriptor(groupRelease(group)) }}</strong>
                                <small>{{ releaseItemNumber(groupRelease(group)) }} · SKU {{ releaseSkuNumber(groupRelease(group)) }}</small>
                                @if (groupRelease(group).lines?.length) {
                                    <span>{{ groupRelease(group).lines?.[0]?.quantity ?? '-' }} {{ groupRelease(group).lines?.[0]?.uom || '' }}</span>
                                    @if ((groupRelease(group).lines?.length || 0) > 1) { <span class="more-lines">+{{ (groupRelease(group).lines?.length || 0) - 1 }} more item lines</span> }
                                } @else { <small>No item lines</small> }
                            </div>
                        </td>
                        <td>
                            <div class="directory-cell">
                                <strong>{{ date(groupRelease(group).deliveryWindowEnd || groupRelease(group).pickupWindowStart) }}</strong>
                                <p-tag [value]="planningState(groupRelease(group))" [severity]="planningSeverity(groupRelease(group))"></p-tag>
                                <p-tag [value]="groupRelease(group).releaseStatus || 'DRAFT'" [severity]="statusSeverity(groupRelease(group).releaseStatus)"></p-tag>
                                <small>{{ legExecutionSummary(group) }}</small>
                            </div>
                        </td>
                    </tr>
                    @if (isGroupExpanded(group.key)) {
                        <tr class="directory-detail-row"><td colspan="6">
                            <div class="group-body">
                                <div class="parent-grid">
                                    <div class="detail-card">
                                        <span>Demand</span>
                                        <strong>{{ releaseItemNumber(groupRelease(group)) }}</strong>
                                        <small>SKU {{ releaseSkuNumber(groupRelease(group)) }}</small>
                                        <small>{{ releaseItemDescriptor(groupRelease(group)) }}</small>
                                        <small>{{ (groupRelease(group).lines || []).length }} line{{ (groupRelease(group).lines || []).length === 1 ? '' : 's' }}</small>
                                    </div>
                                    <div class="detail-card">
                                        <span>Pickup</span>
                                        <strong>{{ pickupStoreLabel(groupRelease(group)) }}</strong>
                                        <small>{{ pickupStoreDetail(groupRelease(group)) }}</small>
                                    </div>
                                    <div class="detail-card">
                                        <span>Dropoff</span>
                                        <strong>{{ dropoffStoreLabel(groupRelease(group)) }}</strong>
                                        <small>{{ dropoffStoreDetail(groupRelease(group)) }}</small>
                                    </div>
                                    <div class="detail-card">
                                        <span>Planning</span>
                                        @if (groupRelease(group).plannedShipmentId) {
                                            <a class="shipment-link" [routerLink]="['/shipment/view', groupRelease(group).plannedShipmentId]">
                                                {{ linkedShipmentLabel(groupRelease(group)) }}
                                            </a>
                                        } @else {
                                            <strong>{{ linkedShipmentLabel(groupRelease(group)) }}</strong>
                                        }
                                        <small>{{ releaseDeliveryLabel(groupRelease(group)) }}</small>
                                        <small>{{ planningNote(groupRelease(group)) }}</small>
                                    </div>
                                    <div class="detail-card">
                                        <span>Window</span>
                                        <strong>Pickup {{ date(groupRelease(group).pickupWindowStart || groupRelease(group).pickupWindowEnd) }}</strong>
                                        <small>Delivery {{ date(groupRelease(group).deliveryWindowStart || groupRelease(group).deliveryWindowEnd) }}</small>
                                    </div>
                                    <div class="detail-card">
                                        <span>Execution Note</span>
                                        <strong>{{ planningReason(groupRelease(group)) }}</strong>
                                        <small>{{ linkedStoreNames(groupRelease(group)) }}</small>
                                    </div>
                                </div>

                                @if (group.children.length) {
                                    <div class="child-section">
                                        <div class="child-section-header">
                                            <div>
                                                <div class="child-section-title">Orchestration Legs</div>
                                                <div class="child-section-copy">Child releases generated from the original POSM release for cross-dock execution.</div>
                                            </div>
                                            <div class="child-section-summary">{{ legExecutionSummary(group) }}</div>
                                        </div>

                                        <div class="child-list">
                                            @for (leg of group.children; track leg.releaseId || leg.releaseNumber || $index) {
                                                <div class="child-row">
                                                    <div class="child-row-header">
                                                        <div>
                                                            <div class="child-row-title">
                                                                <a class="release-link" [routerLink]="['/transport-planning/releases', leg.releaseId]">
                                                                    {{ leg.releaseNumber || ('Release #' + (leg.releaseId || '-')) }}
                                                                </a>
                                                                <p-tag [value]="legLabel(leg)" severity="info"></p-tag>
                                                                @if (leg.crossDockLegType) {
                                                                    <p-tag [value]="leg.crossDockLegType" severity="secondary"></p-tag>
                                                                }
                                                            </div>
                                                            <div class="child-row-meta">
                                                                <span>{{ leg.sourceType }} {{ leg.sourceNumber || leg.sourceId || '' }}</span>
                                                                @if (leg.customerTrackingNumber) {
                                                                    <span>Tracking {{ leg.customerTrackingNumber }}</span>
                                                                }
                                                            </div>
                                                        </div>
                                                        <div class="child-row-status">
                                                            <p-tag [value]="planningState(leg)" [severity]="planningSeverity(leg)"></p-tag>
                                                            <p-tag [value]="leg.releaseStatus || 'DRAFT'" [severity]="statusSeverity(leg.releaseStatus)"></p-tag>
                                                        </div>
                                                    </div>

                                                    <div class="child-row-grid">
                                                        <div class="detail-card compact">
                                                            <span>Demand</span>
                                                            <strong>{{ releaseItemNumber(leg) }}</strong>
                                                            <small>SKU {{ releaseSkuNumber(leg) }}</small>
                                                            <small>{{ releaseItemDescriptor(leg) }}</small>
                                                            <small>{{ (leg.lines || []).length }} line{{ (leg.lines || []).length === 1 ? '' : 's' }}</small>
                                                        </div>
                                                        <div class="detail-card compact">
                                                            <span>Pickup</span>
                                                            <strong>{{ pickupStoreLabel(leg) }}</strong>
                                                            <small>{{ pickupStoreDetail(leg) }}</small>
                                                        </div>
                                                        <div class="detail-card compact">
                                                            <span>Dropoff</span>
                                                            <strong>{{ dropoffStoreLabel(leg) }}</strong>
                                                            <small>{{ dropoffStoreDetail(leg) }}</small>
                                                        </div>
                                                        <div class="detail-card compact">
                                                            <span>Planning</span>
                                                            @if (leg.plannedShipmentId) {
                                                                <a class="shipment-link" [routerLink]="['/shipment/view', leg.plannedShipmentId]">
                                                                    {{ linkedShipmentLabel(leg) }}
                                                                </a>
                                                            } @else {
                                                                <strong>{{ linkedShipmentLabel(leg) }}</strong>
                                                            }
                                                            <small>{{ releaseDeliveryLabel(leg) }}</small>
                                                            <small>{{ planningNote(leg) }}</small>
                                                        </div>
                                                        <div class="detail-card compact">
                                                            <span>Window</span>
                                                            <strong>Pickup {{ date(leg.pickupWindowStart || leg.pickupWindowEnd) }}</strong>
                                                            <small>Delivery {{ date(leg.deliveryWindowStart || leg.deliveryWindowEnd) }}</small>
                                                        </div>
                                                        <div class="detail-card compact">
                                                            <span>Execution Note</span>
                                                            <strong>{{ planningReason(leg) }}</strong>
                                                            <small>{{ assignmentSummary(leg) }}</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        </td></tr>
                    }
                </ng-template>
            </p-table>
            </div>
        }
    </app-page-data-panel>
</div>
